export type PercentagesMap = Record<string, number>;

export type NormalizedLabel = {
  display: string; // Original case for UI display
  key: string; // Key for data storage (originalQuestionName for group questions, lowercase label otherwise)
};

export const computeTotal = (percentages: PercentagesMap): number =>
  Object.values(percentages).reduce((acc, curr) => acc + curr, 0);

export const clampForLabel = (
  percentages: PercentagesMap,
  label: string,
  maxOverall = 100,
) => {
  const total = computeTotal(percentages);
  const current = percentages[label] || 0;
  return maxOverall - total + current; // remaining allowance for this label
};

/**
 * Build the initial map of label -> percentage value from a flattened answers object.
 * We expect individual answers to live at keys shaped like `${questionName}[${key}]`.
 * Any non-numeric, negative, null or undefined values are normalised to 0.
 * Returns a map with normalized lowercase keys for consistency.
 */
export const buildInitialPercentages = (
  normalizedLabels: NormalizedLabel[],
  answers: Record<string, unknown> | undefined,
  questionName: string,
): PercentagesMap => {
  const result: PercentagesMap = {};
  normalizedLabels.forEach(({ key }) => {
    const answerKey = `${questionName}[${key}]`;
    const raw = answers?.[answerKey];
    const num = Number(raw);
    result[key] = Number.isFinite(num) && num >= 0 ? num : 0;
  });
  return result;
};

/**
 * Build the initial map of label -> percentage value for a percentage group question.
 * This variant is used when answers are stored using original question names from the group,
 * rather than bracketed keys. Values are expected in the format "10%" and are parsed to numbers.
 * Any non-numeric, negative, null or undefined values are normalised to 0.
 * Returns a map with the same keys used in normalizedLabels.
 */
export const buildInitialGroupPercentages = (
  normalizedLabels: NormalizedLabel[],
  answers: Record<string, unknown> | undefined,
): PercentagesMap => {
  const result: PercentagesMap = {};
  normalizedLabels.forEach(({ key }) => {
    const rawValue = answers?.[key];

    // Only process primitive values (string, number)
    const isPrimitive =
      typeof rawValue === "string" || typeof rawValue === "number";
    if (!isPrimitive) {
      result[key] = 0;
      return;
    }

    const stringValue = String(rawValue);
    const numericValue = Number(stringValue.replace(/%/g, ""));
    result[key] =
      Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
  });
  return result;
};
